import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { ElephantLogo } from "@/components/ElephantLogo";
import { useAuth } from "@/context/AuthContext";
import { useLang, STR } from "@/i18n/translations";
import api from "@/lib/api";

export default function AdminLogin() {
  const { user, setUser, loading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user?.role === "admin") navigate("/admin", { replace: true });
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      if (data.role === "admin") navigate("/admin", { replace: true });
      else setError("Ce compte n'a pas accès à l'administration.");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Connexion échouée.");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-obsidian grain px-5">
      <div className="w-full max-w-md card-dark rounded-2xl p-10" data-testid="admin-login-card">
        <div className="flex flex-col items-center mb-8">
          <ElephantLogo className="w-14 h-14 mb-4" />
          <h1 className="font-serif text-3xl">Sily <span className="text-gold">Agency</span></h1>
          <p className="eyebrow mt-2">{t(STR.admin.login)}</p>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="admin-email-input"
                 className="w-full bg-white/5 border border-gold/20 rounded px-4 py-3 text-sm outline-none focus:border-gold" />
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t({ fr: "Mot de passe", en: "Password" })} data-testid="admin-password-input"
                 className="w-full bg-white/5 border border-gold/20 rounded px-4 py-3 text-sm outline-none focus:border-gold" />
          {error && <p className="text-crimson-accent text-sm" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="admin-login-btn" className="btn-gold rounded-full w-full py-3.5 flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {t({ fr: "Se connecter", en: "Sign in" })}
          </button>
        </form>

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-white/10" /><span className="text-xs text-cream/40">{t({ fr: "ou", en: "or" })}</span><div className="flex-1 h-px bg-white/10" />
        </div>

        <button onClick={googleLogin} data-testid="admin-google-btn" className="w-full border border-gold/25 rounded-full py-3.5 flex items-center justify-center gap-3 text-cream hover:border-gold transition-colors">
          <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 5c1.6 0 3 .5 4.1 1.6l3-3A9.8 9.8 0 0012 2 10 10 0 002.4 8l3.5 2.7A6 6 0 0112 5z"/><path fill="#4285F4" d="M22 12c0-.7-.1-1.4-.2-2H12v4h5.6a4.8 4.8 0 01-2 3.1l3.4 2.7A9.9 9.9 0 0022 12z"/><path fill="#FBBC05" d="M6 12c0-.7.1-1.4.3-2L2.8 7.3A10 10 0 002 12c0 1.6.4 3.2 1 4.6l3.5-2.7A6 6 0 016 12z"/><path fill="#34A853" d="M12 22c2.7 0 5-1 6.6-2.5l-3.4-2.7c-.9.6-2 1-3.2 1a6 6 0 01-5.6-4L2.8 16.6A10 10 0 0012 22z"/></svg>
          {t(STR.admin.signInGoogle)}
        </button>
      </div>
    </div>
  );
}
